import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { EstadoOrdenServicio } from '@/lib/supabase/tipos-bd'

import { encolarOrden } from '@/lib/campo/colaDeOrdenes'

import type { NuevaOrdenServicio, OrdenServicioConRelaciones } from './tipos'

/**
 * Columnas que la matriz necesita, con sus relaciones resueltas en una sola
 * consulta.
 *
 * Se declara aparte y no en línea porque la usan la lista y el detalle: si las
 * dos piden lo mismo, react-query reutiliza lo que ya tiene en memoria y abrir
 * una orden desde la matriz es instantáneo.
 */
const SELECCION = `
  *,
  cliente:clientes ( id, nombre, documento, ciudad, departamento ),
  servicio:servicios ( id, nombre, codigo ),
  zona:zonas ( id, nombre, ciudad, departamento ),
  cotiza_colaborador:colaboradores!ordenes_servicio_cotiza_colaborador_id_fkey ( id, nombre ),
  
  plan_modulo:plan_modulos (
    id, codigo, nombre, avance, horas, elementos, alistamiento_estado, alistamiento_fecha,
    etapa:plan_etapas ( id, nombre, orden, proyecto:proyectos_plan ( id, codigo, nombre, cliente, ubicacion, latitud, longitud, radio_geocerca_m ) ),
    colaboradores:plan_modulos_colaboradores (
      colaborador_id,
      avance_porcentaje,
      completado,
      horas_trabajadas,
      colaborador:colaboradores ( id, nombre )
    )
  ),
  proyecto_plan:proyectos_plan ( id, codigo, nombre, cliente, ubicacion, latitud, longitud, radio_geocerca_m )
,
  cotizacion:cotizaciones (
    id, numero_cotizacion, estado, valor_total, horas_proyectadas, wo_id_asiento, wo_id_vendedor,
    notas_decision,
    es_adicional, fecha_cotizacion,
    wo:wo_cotizaciones ( total, prefijo, vendedor_nombre, fecha ),
    orden_trabajo:ordenes_trabajo (
      id, numero, estado, colaborador_id,
      colaborador:colaboradores ( id, nombre ),
      horas_asignadas, horas_ejecutadas, horas_extra,
      fecha_inicio_programada, fecha_fin_programada,
      solicita_mas_tiempo, horas_adicionales_solicitadas,
      solicita_inicio_sin_ubicacion,
      justificacion_mas_tiempo, notas_precierre,
      resumen_trabajo, resumen_acta,
      acta_enviada_en, acta_firmada_en, acta_wa_enviada_en, acta_envio_error
    )
  )
`

const CLAVE_BASE = ['ordenes-servicio'] as const

/**
 * Toma el primer elemento cuando Supabase devuelve una relación como lista.
 *
 * Una orden de servicio puede tener varias cotizaciones a lo largo del tiempo
 * —se rechaza una y se recotiza—, así que la relación es de uno a muchos y
 * llega como arreglo aunque solo haya una viva. Compararla contra null nunca
 * daba verdadero y el tablero salía vacío con las órdenes cargadas.
 */
function primero<T>(valor: unknown): T | null {
  if (Array.isArray(valor)) return (valor[0] as T) ?? null
  return (valor as T) ?? null
}

/**
 * Deja la cotización vigente y su orden de trabajo al primer nivel.
 *
 * De las cotizaciones de una orden solo interesa la que sigue viva: las
 * rechazadas son historia y viven en la bitácora. La orden de trabajo llega
 * anidada dentro de la cotización porque así es la relación, pero para el
 * tablero se necesita al lado: una orden es una tarjeta, y su estado puede
 * venir de cualquiera de los tres documentos.
 */
function aplanar(fila: Record<string, unknown>): OrdenServicioConRelaciones {
  const cotizaciones = Array.isArray(fila.cotizacion)
    ? (fila.cotizacion as Record<string, unknown>[])
    : fila.cotizacion
    ? [fila.cotizacion as Record<string, unknown>]
    : []

  /*
   * LOS ADICIONALES NO SON LA COTIZACIÓN DE LA ORDEN.
   *
   * Se cuelgan de una obra que ya está andando y tienen su propio panel. Al
   * no distinguirlos, una orden con su cotización APROBADA y un adicional a
   * medias mostraba el adicional en el sitio de la principal: la ficha decía
   * «cotización a medias, con Seguir cotizando se retoma» —un botón que no
   * podía salir, porque la orden ya estaba cotizada y con razón—. Se veía en
   * la OS-2026-0047.
   */
  const principales = cotizaciones.filter((c) => !c.es_adicional)

  const vigente =
    principales.find((c) => {
      const ot = primero<Record<string, unknown>>(c.orden_trabajo)
      return ot !== null && ot !== undefined
    }) ??
    principales.find((c) => c.estado === 'aprobada') ??
    principales.find((c) => c.estado !== 'rechazada' && c.estado !== 'borrador') ??
    principales.find((c) => c.estado !== 'rechazada') ??
    principales[0] ??
    null

  const trabajo = primero<Record<string, unknown>>(vigente?.orden_trabajo)

  return {
    ...(fila as unknown as OrdenServicioConRelaciones),
    cotizacion: vigente
      ? ({
          ...vigente,
          wo: primero<Record<string, unknown>>(vigente.wo) ?? null,
          // EL VALOR VIVE EN WORLD OFFICE, no aquí: cotizaciones.valor_total
          // está en null en las 2.622 filas. La matriz llevaba desde el
          // principio sin mostrar un solo peso porque leía la columna vacía.
          // Es el mismo coalesce que ya hacen las vistas de la base.
          valor_total:
            (vigente.valor_total as number | null) ??
            (primero<{ total: number | string }>(vigente.wo)?.total != null
              ? Number(primero<{ total: number | string }>(vigente.wo)!.total)
              : null),
        } as unknown as OrdenServicioConRelaciones['cotizacion'])
      : null,
    orden_trabajo: trabajo
      ? ({
          ...trabajo,
          // Se aplana igual que la cotización: según cómo resuelva PostgREST la
          // relación, el colaborador puede llegar como objeto o como lista de
          // uno, y una tarjeta que compara contra null fallaría en un caso.
          colaborador: primero(trabajo.colaborador),
        } as unknown as OrdenServicioConRelaciones['orden_trabajo'])
      : null,
    plan_modulo: primero<Record<string, unknown>>(fila.plan_modulo) as any,
    proyecto_plan: primero<Record<string, unknown>>(fila.proyecto_plan) as any,
  }
}

/** Lista completa de órdenes de servicio para la matriz. */
export function useOrdenesServicio() {
  const consulta = useQuery({
    queryKey: [...CLAVE_BASE, 'lista'],
    // Red de seguridad del tiempo real (useOrdenesEnVivo): si la conexión en
    // vivo se cae sin avisar, la matriz se repasa sola cada minuto igual.
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<OrdenServicioConRelaciones[]> => {
      const { data, error } = await supabase
        .from('ordenes_servicio')
        .select(SELECCION)
        // Lo más reciente primero: es lo que se está trabajando hoy.
        .order('creado_en', { ascending: false })

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as Record<string, unknown>[]).map(aplanar)
    },
  })

  return {
    ordenes: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/** Una orden concreta, para la pantalla de detalle. */
export function useOrdenServicio(id: string | undefined) {
  const consulta = useQuery({
    queryKey: [...CLAVE_BASE, 'detalle', id],
    enabled: Boolean(id),
    queryFn: async (): Promise<OrdenServicioConRelaciones | null> => {
      const { data, error } = await supabase
        .from('ordenes_servicio')
        .select(SELECCION)
        .eq('id', id!)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))

      return data ? aplanar(data as unknown as Record<string, unknown>) : null
    },
  })

  return {
    orden: consulta.data ?? null,
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/** Crear una orden y mover su estado por el recorrido. */
export function useAccionesOrdenServicio() {
  const clienteConsultas = useQueryClient()

  async function refrescar() {
    await clienteConsultas.invalidateQueries({ queryKey: CLAVE_BASE })
  }

  const crear = useMutation({
    // Devuelve el id ADEMÁS del número. El número es lo que la persona apunta;
    // el id es lo que hace falta para ubicar la dirección en el mapa acto
    // seguido, que es el paso que nunca se daba y por eso las órdenes
    // importadas llevaban semanas sin un punto.
    mutationFn: async (nueva: NuevaOrdenServicio): Promise<{ id: string; numero: string }> => {
      /*
       * SIN SEÑAL LA ORDEN SE LEVANTA IGUAL.
       *
       * El técnico pasa por una obra, ve algo y lo levanta ahí mismo: es el
       * momento en que más barato sale vender. Pero esto escribe en la base,
       * así que sin cobertura el botón fallaba y se perdía la visita entera
       * —el cliente, la dirección, lo que había que hacer—.
       *
       * Ahora se guarda en el teléfono y la orden se crea de verdad cuando
       * vuelva la señal. Mientras tanto lleva un número provisional: hace
       * falta uno para poder seguir cotizando encima, y decir «OS-…» sería
       * mentir, porque el consecutivo lo pone la base.
       */
      if (!navigator.onLine) {
        const enCola = await encolarOrden(nueva)
        return { id: enCola.id, numero: 'Sin número todavía' }
      }

      /*
       * `wo_id_tercero_pendiente` NO ES UNA COLUMNA: es un recado para la cola.
       *
       * Lo lleva la orden guardada sin señal para poder adoptar al cliente
       * cuando suba. Con señal el cliente ya se adoptó un momento antes, así
       * que aquí sobra —y mandarlo rompía el alta entera con «Could not find
       * the 'wo_id_tercero_pendiente' column of 'ordenes_servicio'», un
       * error en inglés y de la tripa de la base, delante del cliente, justo
       * al levantar la obra—. La cola ya lo apartaba; este camino no.
       */
      const { wo_id_tercero_pendiente: _recado, ...paraLaBase } = nueva

      const { data, error } = await supabase
        .from('ordenes_servicio')
        // El número y el creador los pone la base: mandarlos desde aquí sería
        // inventar un consecutivo que puede chocar con otro.
        .insert(paraLaBase as never)
        .select('id, numero')
        .single()

      if (error) throw new Error(mensajeDeError(error))

      return { id: data.id, numero: data.numero }
    },
    onSuccess: refrescar,
  })

  /**
   * Cambio de estado.
   *
   * Qué transiciones son válidas lo decide la base, no esta función: si el
   * salto no está permitido, el trigger lo rechaza con un mensaje en términos
   * del negocio. Aquí solo se pide el cambio.
   */
  const cambiarEstado = useMutation({
    mutationFn: async ({ id, estado }: { id: string; estado: EstadoOrdenServicio }) => {
      const { error } = await supabase
        .from('ordenes_servicio')
        .update({ estado } as never)
        .eq('id', id)

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })

  const editar = useMutation({
    mutationFn: async ({ id, valores }: { id: string; valores: Partial<NuevaOrdenServicio> }) => {
      const { error } = await supabase
        .from('ordenes_servicio')
        .update(valores as never)
        .eq('id', id)

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })

  return { crear, cambiarEstado, editar }
}
